import { useState } from 'react';

import { useFirebase } from '@/firebase';
import type { PrimaryEntityType } from '@/lib/types';

interface GenerateEntityVisualInput {
  novelId: string;
  entityId: string;
  entityType: PrimaryEntityType | 'codex';
  visualDescription: string;
  referenceImageUrls?: string[];
}

interface UseGenerateEntityVisualResult {
  generateEntityVisual: (params: GenerateEntityVisualInput) => Promise<string | null>;
  loading: boolean;
  error: Error | null;
}

/**
 * Hook for generating entity visual images using AI
 */
export function useGenerateEntityVisual(): UseGenerateEntityVisualResult {
  const { backend, user } = useFirebase();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const generateEntityVisual = async (params: GenerateEntityVisualInput): Promise<string | null> => {
    if (!backend || !user) return null;

    setLoading(true);
    setError(null);

    try {
      const result = await backend.ai.generateEntityVisual({
        ...params,
        userId: user.uid,
      });
      return result.imageUrl;
    } catch (err: unknown) {
      const firebaseError = err as { code?: string; message?: string };
      if (firebaseError.code === 'deadline-exceeded') {
        return 'PENDING';
      }

      console.error('Error generating entity visual:', err);
      setError(err as Error);
      return null;
    } finally {
      setLoading(false);
    }
  };

  return { generateEntityVisual, loading, error };
}
